import React, { useState, useEffect, useRef, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { MobileTimePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import './componentreg.css';
import { useForm } from 'react-hook-form';
import { CustomDaySelect } from '../Inputfield/inputfield';
import { shiftgetmodule } from '../../Services/app/shiftservice';
import Swal from 'sweetalert2';
import { Tooltip, Autocomplete } from '@mui/material';
import { customerbasedshift, saveMasterListSafely } from '../../Services/app/masterservice';
import dayjs from 'dayjs';
import { cleanCustomerId, isPmiCustomer } from '../../Services/app/operatorservice';
import { useUserRole } from '../../Shared/hooks/useUserRole';
import { filterPlantsForUser } from '../../Shared/utils/machineGroupAccess';
export default function ComponentAdd({ open, handleClose, handleAdd, dialogOpenCount, datasource, setDatasource, customerId }) {
  const customDaySelectRef = useRef();
  const dialogBackgroundColor = dialogOpenCount === 0 ? '#f7f7f7' : '#ededed';
  const [shiftsmodule, setShiftsmodule] = useState([]);
  const isCustomerId = cleanCustomerId(customerId) === window._env_.BIOTEK_CUSTOMER_ID;

  const componentNameRef = useRef();

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (componentNameRef.current) {
          componentNameRef.current.focus();
        }
      }, 100); // reduced delay for better responsiveness

      return () => clearTimeout(timer); // cleanup on unmount
    }
  }, [open]);

  const { register, handleSubmit, formState: { errors }, trigger, setValue, reset } = useForm({
    shouldFocusError: true,
    mode: 'onBlur'
  });

    const [operationNumberError, setOperationNumberError] = useState('');

    const getOperationNumberDuplicateError = (form) => {
    const norm = (val) => (val ?? '').toString().trim().toLowerCase();
    const list = Array.isArray(datasource) ? datasource : [];
    if (isCustomerId) {
      if (!norm(form.component_number) || !norm(form.operation_number)) return '';
      const comp = list.find((c) => norm(c.component_number) === norm(form.component_number));
      if (!comp) return '';
      const dup = (comp.operations || []).some(
        (op) => norm(op.operation_number) === norm(form.operation_number)
      );
      return dup ? 'This Operation Number already has exists for this Component' : '';
    }
    if (cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID) {
      if (!norm(form.operation_number)) return '';
      const dup = list.some(
        (s) =>
          norm(s.component_name) === norm(form.component_name) &&
          norm(s.item_code) === norm(form.item_code) &&
          norm(s.process_name) === norm(form.process_name) &&
          norm(s.operation_number) === norm(form.operation_number) &&
          norm(s.plant_code) === norm(form.plant_code)
      );
      return dup ? 'Component with same Item Code and Process Name already exists' : '';
    }
    return '';
  };

  const defaultShiftForm = useMemo(() => ({
    component_name: '',
    component_number: '',
    operation_number: '',
    operation_name: '',
    operation_type: '',
    factorval: '',
    factor: '',
    jobcard: '',
    drawingcode: '',
    cycle_time: dayjs('00:00:00', 'HH:mm:ss'),
    handling_time: dayjs('00:00:00', 'HH:mm:ss'),
    setupTime: dayjs('00:00:00', 'HH:mm:ss'),
    item_code: '',
    process_name: '',
    plant_code: '',
    plant_codes: [],
  }), []);

  const [shiftForm, setShiftForm] = useState(defaultShiftForm);
  const [processGroupOptions, setProcessGroupOptions] = useState([]);
  const [plantOptions, setPlantOptions] = useState([]);
  const showPlantField = isPmiCustomer(customerId) && plantOptions.length > 0;
  const { userRole, isSuperAdmin } = useUserRole();
  const userPlantCodes = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userDetails') || '{}').plantAccess || [];
    } catch {
      return [];
    }
  }, []);
  // Non-Super-Admins only ever see the plant(s) assigned to them.
  const visiblePlantOptions = useMemo(
    () => filterPlantsForUser(plantOptions, userRole, userPlantCodes),
    [plantOptions, userRole, userPlantCodes]
  );

  useEffect(() => {
    setOperationNumberError('');
    if (!open) {
      reset(defaultShiftForm);
    } else {
      setShiftForm(defaultShiftForm);
    }
  }, [open, reset, defaultShiftForm]);

  // Non-Super-Admins with exactly one assigned plant don't need to pick —
  // select it for them automatically.
  useEffect(() => {
    if (!open || !showPlantField || isSuperAdmin) return;
    if (visiblePlantOptions.length === 1 && !shiftForm.plant_code) {
      const onlyCode = visiblePlantOptions[0].code;
      setShiftForm((prev) => ({ ...prev, plant_code: onlyCode }));
      setValue('plant_code', onlyCode);
      trigger('plant_code');
    }
  }, [open, showPlantField, isSuperAdmin, visiblePlantOptions, shiftForm.plant_code, setValue, trigger]);

  useEffect(() => {
    getComponentsAdddata();
    if (cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID) {
      fetchProcessGroups();
    }
    fetchPlantGroups();
  }, []);

  const fetchPlantGroups = async () => {
    try {
      if (!isPmiCustomer(customerId)) {
        setPlantOptions([]);
        return;
      }
      const data = await customerbasedshift(customerId, 'plantgroups');
      const allPlantGroups = data?.[0]?.value || [];
      setPlantOptions(allPlantGroups.map((p) => ({ code: p.code, name: p.name || p.code })));
    } catch (error) {
      console.error('Error fetching plant groups:', error);
      setPlantOptions([]);
    }
  };

  const fetchProcessGroups = async () => {
    const key = 'processgroups';
    try {
      const data = await customerbasedshift(customerId, key);
      const allProcessGroups = data[0]?.value || [];
      const mappedGroups = allProcessGroups.map((item) => ({
        value: item.groupName,
        label: item.groupName
      }));
      setProcessGroupOptions(mappedGroups);
    } catch (error) {
      console.error("Error fetching reason groups:", error);
      setProcessGroupOptions([]);
    }
  };

 const getComponentsAdddata = async () => {
  const fallbackOptions = [
    { value: 'Multiplication Factor', label: 'Multiplication Factor' },
    { value: 'Divide Factor', label: 'Divide Factor' },
  ];

  try {
    const response = await shiftgetmodule();

    if (Array.isArray(response) && response.length > 0) {
      const formattedOptions = response.map((item) => ({
        value: item,
        label: item,
      }));

      setShiftsmodule(formattedOptions);
    } else {
      setShiftsmodule(fallbackOptions);
    }
  } catch (error) {
    console.error('Error fetching components:', error);
    setShiftsmodule(fallbackOptions);
  }
};

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    const nextForm = { ...shiftForm, [name]: value };
    setShiftForm(nextForm);
    setValue(name, value);
    trigger(name);
    if (['operation_number', 'component_number', 'component_name', 'item_code', 'process_name'].includes(name)) {
      setOperationNumberError(getOperationNumberDuplicateError(nextForm));
    }
  };

  // Super Admin only: multi-select Plant checkboxes — one Component record is
  // created per selected plant. "all" toggles every plant on/off.
  const handlePlantCodesChange = (event) => {
    const { value } = event.target;
    let nextValues = typeof value === 'string' ? value.split(',') : value;

    if (Array.isArray(nextValues) && nextValues.includes('all')) {
      const allPlantCodes = visiblePlantOptions.map((p) => p.code).filter(Boolean);
      const currentlyAllSelected =
        Array.isArray(shiftForm.plant_codes) && shiftForm.plant_codes.length === allPlantCodes.length;
      nextValues = currentlyAllSelected ? [] : allPlantCodes;
    }

    setShiftForm((prev) => ({ ...prev, plant_codes: nextValues }));
    setValue('plant_codes', nextValues);
    trigger('plant_codes');
  };

  const handleOperationNameChange = (event) => {
    const { name, value } = event.target;
    const noSpaceValue = value.replace(/\s/g, '');
    setShiftForm((prev) => ({ ...prev, [name]: noSpaceValue }));
    setValue(name, noSpaceValue);
    trigger(name);
  };

  const componentNameOptions = useMemo(() => {
    const names = (datasource || [])
      .map((c) => c.component_name)
      .filter((n) => n && String(n).trim() !== '');
    return Array.from(new Set(names));
  }, [datasource]);

    const applyComponentName = (value) => {
    const nextValue = value || '';
    const match = (datasource || []).find(
      (c) => (c.component_name || '').trim().toLowerCase() === nextValue.trim().toLowerCase()
    );
    const autoNumber = match?.component_number;
    const nextForm = {
      ...shiftForm,
      component_name: nextValue,
      ...(autoNumber ? { component_number: autoNumber } : {}),
    };
    setShiftForm(nextForm);
    setValue('component_name', nextValue);
    trigger('component_name');
    if (autoNumber) {
      setValue('component_number', autoNumber);
      trigger('component_number');
    }
    setOperationNumberError(getOperationNumberDuplicateError(nextForm));
  };

  const handleTimeChange = (name, value) => {
    setShiftForm((prevShiftForm) => ({
      ...prevShiftForm,
      [name]: value,
    }));
    setValue(name, value);
    trigger(name);
  };

  const onSubmit = async (data) => {
    try {
      setOperationNumberError('');
      const timeFields = [
        { key: "cycle_time", label: "Cycle Time" },
        { key: "handling_time", label: "Handling Time" },
        { key: "setupTime", label: "Setup Time" },
      ];
      for (const { key, label } of timeFields) {
        const value = data[key];
        if (!value || !dayjs(value).isValid()) {
          handleClose();
          Swal.fire({
            icon: "error",
            title: "Invalid Input",
            text: `${label} has an invalid or incomplete time format.`,
          });
          return;
        }
        if (key === "cycle_time" && dayjs(value).format("HH:mm:ss") === "00:00:00") {
          handleClose();
          Swal.fire({
            icon: "error",
            title: "Invalid Input",
            text: "Cycle Time must not be 00:00:00.",
          });
          return;
        }
      }
      const safeFormat = (v) => dayjs(v).format("HH:mm:ss");
      const id = Math.random().toString(36).substr(2, 9);
      const isGplast = cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID;
      const normalizeVal = (val) => (val || "").trim().toLowerCase();

      if (isCustomerId) {
        const operation = {
          id,
          operation_name: data.operation_name,
          operation_number: data.operation_number,
          cycle_time: safeFormat(data.cycle_time),
          handling_time: safeFormat(data.handling_time),
          setupTime: safeFormat(data.setupTime),
        };

        const key = "component";
        try {
          // Merge onto the FRESH server list (never stale state); the helper
          // re-reads, guards against a wipe, and snapshots the previous list.
          const response = await saveMasterListSafely({
            customerId,
            key,
            baselineCount: Array.isArray(datasource) ? datasource.length : 0,
            mutate: (allComponents) => {
              const componentIndex = allComponents.findIndex(
                (c) => normalizeVal(c.component_number) === normalizeVal(data.component_number)
              );
              if (componentIndex !== -1) {
                const existingOps = allComponents[componentIndex].operations || [];
                const duplicateOp = existingOps.find(
                  (op) => normalizeVal(op.operation_number) === normalizeVal(data.operation_number)
                );
                if (duplicateOp) {
                  const e = new Error("duplicate operation");
                  e.code = "DUP_OP";
                  throw e;
                }
                allComponents[componentIndex] = {
                  ...allComponents[componentIndex],
                  component_name: data.component_name,
                  factor: "",
                  factorval: "",
                  operations: [...existingOps, operation],
                };
              } else {
                allComponents.push({
                  id: Math.random().toString(36).substr(2, 9),
                  component_name: data.component_name,
                  component_number: data.component_number,
                  factor: "",
                  factorval: "",
                  operations: [operation],
                });
              }
              return allComponents;
            },
          });
          Swal.fire(response?.msg || "Component created successfully!", "", "success");
          handleClose();
          reset(defaultShiftForm);
        } catch (e) {
          if (e.code === "DUP_OP") {
            setOperationNumberError("This Operation Number already exists for this Component");
            Swal.fire(
              "Duplicate entry",
              "This Component Number already has this Operation Number",
              "error"
            );
            return;
          }
          throw e; // MasterShrinkError / network — handled by the outer catch
        }
        return;
      }

      const buildShiftData = (plantCode) => ({
        id: Math.random().toString(36).substr(2, 9),
        component_name: data.component_name,
        component_number: data.component_number,
        operation_number: data.operation_number,
        operation_name: data.operation_name,
        operation_type: data.operation_type,
        factorval: data.factorval,
        jobcard: data.jobcard,
        drawingcode: data.drawingcode,
        factor: data.factor,
        cycle_time: safeFormat(data.cycle_time),
        handling_time: safeFormat(data.handling_time),
        setupTime: safeFormat(data.setupTime),
        ...(cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID && {
          item_code: data.item_code,
          process_name: data.process_name,
        }),
        ...(showPlantField && {
          plant_code: plantCode,
        }),
      });

      const normalize = (val) => (val || "").trim().toLowerCase();
      // Super Admin can pick several plants at once — one record is created
      // per plant. Everyone else keeps the single-plant behavior.
      const targetPlantCodes = showPlantField
        ? isSuperAdmin
          ? (data.plant_codes || []).filter(Boolean)
          : [data.plant_code || undefined]
        : [undefined];
      try {
        // Merge onto the FRESH server list instead of the (possibly stale/empty)
        // `datasource` prop — that stale read is what wiped the whole list down
        // to the single new item. The helper re-reads, guards the shrink, and
        // snapshots the previous list before writing.
        const response = await saveMasterListSafely({
          customerId,
          key: "component",
          baselineCount: Array.isArray(datasource) ? datasource.length : 0,
          mutate: (existingShifts) => {
            if (isGplast) {
              const duplicatePlants = targetPlantCodes.filter((pc) =>
                existingShifts.some(
                  (shift) =>
                    normalize(shift.component_name) === normalize(data.component_name) &&
                    normalize(shift.item_code) === normalize(data.item_code) &&
                    normalize(shift.process_name) === normalize(data.process_name) &&
                    normalize(shift.operation_number) === normalize(data.operation_number) &&
                    (shift.plant_code || undefined) === pc
                )
              );
              if (duplicatePlants.length > 0) {
                const e = new Error("duplicate");
                e.code = "DUP_GPLAST";
                throw e;
              }
            } else {
              const duplicatePlants = targetPlantCodes.filter((pc) =>
                existingShifts.some(
                  (shift) =>
                    normalize(shift.component_number) === normalize(data.component_number) &&
                    (shift.plant_code || undefined) === pc
                )
              );
              if (duplicatePlants.length > 0) {
                const e = new Error("duplicate");
                e.code = "DUP_NUMBER";
                throw e;
              }
            }
            targetPlantCodes.forEach((pc) => existingShifts.push(buildShiftData(pc)));
            return existingShifts;
          },
        });

        Swal.fire(
          response?.msg ||
            (targetPlantCodes.length > 1
              ? `Component created successfully for ${targetPlantCodes.length} plants!`
              : "Component created successfully!"),
          "",
          "success"
        );
        handleClose();
        reset(defaultShiftForm);
      } catch (e) {
        if (e.code === "DUP_GPLAST") {
          setOperationNumberError("Component with same Item Code and Process Name already exists");
          Swal.fire("Duplicate entry", "Component with same Item Code and Process Name already exists", "error");
          return;
        }
        if (e.code === "DUP_NUMBER") {
          handleClose();
          Swal.fire("Duplicate entry", "Component Number already exists", "error");
          return;
        }
        throw e; // MasterShrinkError / network — handled by the outer catch
      }
    } catch (error) {
      // Data-loss guard tripped: the existing list couldn't be read, so saving
      // would have erased it. Nothing was written — keep the dialog open so the
      // operator doesn't lose their input, and tell them to retry.
      if (error.code === "MASTER_SHRINK_GUARD") {
        Swal.fire(
          "Save blocked to prevent data loss",
          "The existing component list couldn't be read correctly, so saving now would have erased it. Nothing was changed — please check your connection and try again.",
          "warning"
        );
        return;
      }
      console.error("Error submitting data:", error);
      handleClose();
      reset(defaultShiftForm);
      Swal.fire("Error", `Error submitting data: ${error.message}`, "error");
    }
  };


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="300px" PaperProps={{ style: { backgroundColor: dialogBackgroundColor } }}>
      <DialogTitle style={{ color: 'black' }}>Add Component</DialogTitle>
      <div className="close_modal">
        <Tooltip title="Close">
          <IconButton aria-label="close" onClick={handleClose} style={{ backgroundColor: '#ffffff' }}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className="machinedialog">
        <div className="filter_sec">
          <form onSubmit={handleSubmit(onSubmit)} className="form_sec shift_form" autoComplete="off">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="form_sec_fields">
                {/* Component Name Field */}
                {isCustomerId ? (
                  <div className={`form_field ${errors.component_name ? 'error-outline' : ''}`}>
                    <Autocomplete
                      freeSolo
                      disableClearable
                      selectOnFocus
                      handleHomeEndKeys
                      options={componentNameOptions}
                      inputValue={shiftForm.component_name || ''}
                      onInputChange={(event, newValue) => applyComponentName(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputRef={componentNameRef}
                          {...register("component_name", {
                            required: "Component Name is required",
                            maxLength: {
                              value: 100,
                              message: "Maximum length is 100 characters"
                            }
                          })}
                          label="Component Name"
                          name="component_name"
                          error={!!errors.component_name}
                          InputLabelProps={{
                            required: true,
                            sx: {
                              color: 'black',
                              '&.Mui-focused': {
                                color: 'orange',
                              },
                            },
                          }}
                          inputProps={{ ...params.inputProps, maxLength: 100 }}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: 'black',
                              },
                              '&:hover fieldset': {
                                borderColor: 'black',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'orange',
                              },
                              '& .MuiOutlinedInput-input': {
                                color: 'black',
                              },
                              '&.Mui-focused .MuiOutlinedInput-input': {
                                caretColor: 'orange',
                              },
                              '&::placeholder': {
                                color: 'black',
                                opacity: 1,
                              },
                            },
                          }}
                        />
                      )}
                    />
                    {errors.component_name && <div className="mat-error">{errors.component_name.message}</div>}
                  </div>
                ) : (
                  <div className={`form_field ${errors.component_name ? 'error-outline' : ''}`}>
                    <TextField
                      inputRef={componentNameRef}
                      {...register("component_name", {
                        required: "Component Name is required",
                        maxLength: {
                          value: 100,
                          message: "Maximum length is 100 characters"
                        }
                      })}
                      label="Component Name"
                      type="text"
                      name="component_name"
                      value={shiftForm.component_name}
                      onChange={handleFormChange}
                      error={!!errors.component_name}
                      InputLabelProps={{
                        required: true,
                        sx: {
                          color: 'black',
                          '&.Mui-focused': {
                            color: 'orange',
                          },
                        },
                      }}
                      inputProps={{ maxLength: 100 }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'black',
                          },
                          '&:hover fieldset': {
                            borderColor: 'black',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'orange',
                          },
                          '& .MuiOutlinedInput-input': {
                            color: 'black',
                          },
                          '&.Mui-focused .MuiOutlinedInput-input': {
                            caretColor: 'orange',
                          },
                          '&::placeholder': {
                            color: 'black',
                            opacity: 1,
                          },
                        },
                      }}
                    />
                    {errors.component_name && <div className="mat-error">{errors.component_name.message}</div>}
                  </div>
                )}
                {(cleanCustomerId(customerId) != window._env_.GPLAST_CUSTOMER_ID) && (
                  <div className={`form_field ${errors.component_number ? 'error-outline' : ''}`}>
                    <TextField
                      {...register("component_number", {
                        required: "Component Number is required",
                        maxLength: {
                          value: 100,
                          message: "Maximum length is 100 characters"
                        }
                      })}
                      label="Component Number"
                      type="text"
                      name="component_number"
                      value={shiftForm.component_number}
                      onChange={handleFormChange}
                      error={!!errors.component_number}
                      InputLabelProps={{
                        required: true,
                        sx: {
                          color: 'black',
                          '&.Mui-focused': {
                            color: 'orange',
                          },
                        },
                      }}
                      inputProps={{ maxLength: 100 }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'black',
                          },
                          '&:hover fieldset': {
                            borderColor: 'black',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'orange',
                          },
                          '& .MuiOutlinedInput-input': {
                            color: 'black',
                          },
                          '&.Mui-focused .MuiOutlinedInput-input': {
                            caretColor: 'orange',
                          },
                          '&::placeholder': {
                            color: 'black',
                            opacity: 1,
                          },
                        },
                      }}
                    />
                    {errors.component_number && (
                      <div className="mat-error">{errors.component_number.message}</div>
                    )}
                  </div>
                )}
                {showPlantField && (isSuperAdmin ? (
                  <div className={`form_field ${errors.plant_codes ? 'error-outline' : ''}`}>
                    <CustomDaySelect
                      {...register("plant_codes", {
                        validate: (value) => (value && value.length > 0) || "Select at least one plant",
                      })}
                      name="plant_codes"
                      value={shiftForm.plant_codes}
                      onChange={handlePlantCodesChange}
                      label="Select Plant(s)"
                      required={true}
                      multiple
                      showSelectAll={visiblePlantOptions.length > 1}
                      options={visiblePlantOptions.map((p) => ({ value: p.code, label: p.name }))}
                      error={!!errors.plant_codes}
                    />
                    {errors.plant_codes && <div className="mat-error">Select at least one plant</div>}
                  </div>
                ) : (
                  <div className={`form_field ${errors.plant_code ? 'error-outline' : ''}`}>
                    <CustomDaySelect
                      {...register("plant_code", { required: "Plant is required" })}
                      onBlur={() => trigger('plant_code')}
                      name="plant_code"
                      value={shiftForm.plant_code}
                      onChange={handleFormChange}
                      label="Select Plant"
                      required={true}
                      options={visiblePlantOptions.map((p) => ({ value: p.code, label: p.name }))}
                      error={!!errors.plant_code}
                    />
                    {errors.plant_code && <div className="mat-error">{errors.plant_code.message}</div>}
                  </div>
                ))}
                {isCustomerId && (
                  <>
                    <div className={`form_field ${errors.operation_name ? 'error-outline' : ''}`}>
                      <TextField
                        {...register("operation_name", {
                          required: "Operation Name is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum length is 100 characters"
                          }
                        })}
                        label="Operation Name"
                        type="text"
                        name="operation_name"
                        value={shiftForm.operation_name || ""}
                        onChange={handleOperationNameChange}
                        error={!!errors.operation_name}
                        InputLabelProps={{
                          required: true,
                          sx: { color: 'black', '&.Mui-focused': { color: 'orange' } },
                        }}
                        inputProps={{ maxLength: 100 }}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'black' },
                            '&:hover fieldset': { borderColor: 'black' },
                            '&.Mui-focused fieldset': { borderColor: 'orange' },
                            '& .MuiOutlinedInput-input': { color: 'black' },
                            '&.Mui-focused .MuiOutlinedInput-input': { caretColor: 'orange' },
                          },
                        }}
                      />
                      {errors.operation_name && <div className="mat-error">{errors.operation_name.message}</div>}
                    </div>
                    <div className={`form_field ${errors.operation_number || operationNumberError ? 'error-outline' : ''}`}>
                      <TextField
                        {...register("operation_number", {
                          required: "Operation Number is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum length is 100 characters"
                          }
                        })}
                        label="Operation Number"
                        type="text"
                        name="operation_number"
                        value={shiftForm.operation_number || ""}
                        onChange={handleFormChange}
                        error={!!errors.operation_number || !!operationNumberError}
                        InputLabelProps={{
                          required: true,
                          sx: { color: 'black', '&.Mui-focused': { color: 'orange' } },
                        }}
                        inputProps={{ maxLength: 100 }}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'black' },
                            '&:hover fieldset': { borderColor: 'black' },
                            '&.Mui-focused fieldset': { borderColor: 'orange' },
                            '& .MuiOutlinedInput-input': { color: 'black' },
                            '&.Mui-focused .MuiOutlinedInput-input': { caretColor: 'orange' },
                          },
                        }}
                      />
                      {errors.operation_number && <div className="mat-error">{errors.operation_number.message}</div>}
                      {operationNumberError && <div className="mat-error">{operationNumberError}</div>}
                    </div>
                  </>
                )}
                {(cleanCustomerId(customerId) === window._env_.ATECH_CUSTOMER_ID || cleanCustomerId(customerId) === window._env_.HITECH_CUSTOMER_ID) && (
                  <div className={`form_field ${errors.operation_type ? 'error-outline' : ''}`}>
                    <CustomDaySelect
                      {...register("operation_type", { required: "Operation type is required" })}
                      onBlur={() => trigger('operation_type')}
                      name="operation_type"
                      value={shiftForm.operation_type}
                      onChange={handleFormChange}
                      label="Operation Type"
                      required={true}
                      options={[
                        { value: 'VMC', label: 'VMC' },
                        { value: 'HMC', label: 'HMC' },
                        { value: 'CNC', label: 'CNC' },
                        { value: 'TC', label: 'TC' },
                      ]}
                      error={!!errors.operation_type}
                      sx={{
                        '& .MuiInputLabel-root': {
                          top: '-6px',
                          backgroundColor: '#ededed',
                          padding: '0 4px',
                        },
                      }}
                    />
                    {errors.operation_type && <div className="mat-error">{errors.operation_type.message}</div>}
                  </div>
                )}


                {/* Operation Number Field 
                <div className={`form_field ${errors.operation_number ? 'error-outline' : ''}`}>
                  <TextField
                   {...register("operation_number", {
                    required: "Mould Number is required",
                    maxLength: {
                      value: 100,
                      message: "Maximum length is 100 characters"
                    },
                    setValueAs: v => v === "" ? "" : Number(v), // Converts to number or empty
                    validate: value => {
                      if (isNaN(value)) return "Mould Number must be a number";
                      if (value <= 0) return "Mould Number must be greater than 0";
                      return true;
                    }
                  })}
                  
                    label="Mould Number"
                    type="number"
                    name="operation_number"
                    value={shiftForm.operation_number}
                    onChange={handleFormChange}
                    error={!!errors.operation_number}
                    inputProps={{ maxLength: 100, min: 1 }}
                    InputLabelProps={{
                      required: true, 
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.operation_number && <div className="mat-error">{errors.operation_number.message}</div>}
                </div>  */}

                {/* Operation Name Field 
             
                 <div className={`form_field  ${errors.operation_name ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("operation_name", { required: "Mould Name is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      validate: value =>
                        /^[a-zA-Z0-9\s]*$/.test(value) || "Special characters are not allowed"
                    })}
                    onBlur={() => trigger('operation_name')}
                    label="Mould Name"
                    type="text"
                    name="operation_name"
                    value={shiftForm.operation_name}
                    onChange={handleFormChange}
                    error={!!errors.operation_name}
                    inputProps={{ maxLength: 100 }}
                    InputLabelProps={{
                      required: true, 
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.operation_name && (
  <div className="mat-error">{errors.operation_name.message}</div>
)}
                </div>  */}



                {cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID && (
                  <>
                    <div className={`form_field ${errors.item_code ? 'error-outline' : ''}`}>
                      <TextField
                        {...register("item_code", {
                          required: "Item Code is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum length is 100 characters"
                          },
                        })}
                        label="Item Code"
                        type="text"
                        name="item_code"
                        value={shiftForm.item_code || ""}
                        onChange={handleFormChange}
                        error={!!errors.item_code}
                        InputLabelProps={{
                          required: true,
                          sx: {
                            color: "black",
                            "&.Mui-focused": {
                              color: "orange",
                            },
                          },
                        }}
                        inputProps={{ maxLength: 100 }}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "black",
                            },
                            "&:hover fieldset": {
                              borderColor: "black",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "orange",
                            },
                            "& .MuiOutlinedInput-input": {
                              color: "black",
                            },
                            "&.Mui-focused .MuiOutlinedInput-input": {
                              caretColor: "orange",
                            },
                            "&::placeholder": {
                              color: "black",
                              opacity: 1,
                            },
                          },
                        }}
                      />
                      {errors.item_code && (
                        <div className="mat-error">{errors.item_code.message}</div>
                      )}
                    </div>
                    <div className={`form_field ${errors.process_name ? "error-outline" : ""}`}>
                      <CustomDaySelect
                        {...register("process_name", { required: "Process Name is required" })}
                        onBlur={() => trigger("process_name")}
                        name="process_name"
                        value={shiftForm.process_name || ""}
                        onChange={handleFormChange}
                        label="Process Name"
                        required={true}
                        options={processGroupOptions}
                        error={!!errors.process_name}
                      />
                      {errors.process_name && (
                        <div className="mat-error">{errors.process_name.message}</div>
                      )}
                    </div>
                    <div className={`form_field ${errors.operation_number || operationNumberError ? 'error-outline' : ''}`}>
                      <TextField
                        {...register("operation_number", {
                          required: "Operation Number is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum length is 100 characters"
                          },
                        })}
                        label="Operation Number"
                        type="text"
                        name="operation_number"
                        value={shiftForm.operation_number || ""}
                        onChange={handleFormChange}
                        error={!!errors.operation_number || !!operationNumberError}
                        InputLabelProps={{
                          required: true,
                          sx: {
                            color: "black",
                            "&.Mui-focused": {
                              color: "orange",
                            },
                          },
                        }}
                        inputProps={{ maxLength: 100 }}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "black",
                            },
                            "&:hover fieldset": {
                              borderColor: "black",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "orange",
                            },
                            "& .MuiOutlinedInput-input": {
                              color: "black",
                            },
                            "&.Mui-focused .MuiOutlinedInput-input": {
                              caretColor: "orange",
                            },
                            "&::placeholder": {
                              color: "black",
                              opacity: 1,
                            },
                          },
                        }}
                      />
                      {errors.operation_number && (
                        <div className="mat-error">{errors.operation_number.message}</div>
                      )}
                      {operationNumberError && (
                        <div className="mat-error">{operationNumberError}</div>
                      )}
                    </div>
                  </>
                )}
                {!isCustomerId && (<>
                <div className={`form_field  ${errors.factorval ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("factorval", {
                      maxLength: {
                        value: 4,
                        message: "Maximum length is 4 digits"
                      },
                      setValueAs: v => v === "" ? "" : Number(v),
                      validate: value => {
                        if (value === "" || value === null || value === undefined) {
                          return "Factor is required";
                        }
                        if (isNaN(value)) {
                          return "Factor must be a number";
                        }
                        if (value <= 0) {
                          return "Factor must be greater than 0";
                        }
                        if (value.toString().length > 4) {
                          return "Maximum 4 digits allowed";
                        }
                        return true;
                      }
                    })}

                    onBlur={() => trigger('factorval')}
                    label="Factor Value"
                    type="number"
                    name="factorval"
                    value={shiftForm.factorval}
                    onChange={handleFormChange}
                    error={!!errors.factorval}
                    inputProps={{ maxLength: 4, min: 1 }}
                    InputLabelProps={{
                      required: true,
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.factorval && <div className="mat-error">{errors.factorval.message}</div>} {/* Changed to div and mat-error */}
                </div>
                <div className={`form_field  ${errors.factor ? 'error-outline' : ''}`}>
                  <CustomDaySelect
                    {...register("factor", { required: "Factor is required" })}
                    onBlur={() => trigger('factor')}
                    ref={customDaySelectRef}
                    name="factor"
                    value={shiftForm.factor}
                    onChange={handleFormChange}
                    label="Select Factor"
                    required={true}
                    options={shiftsmodule}
                    error={!!errors.factor}
                  />
                  {errors.factor && <div className="mat-error">{errors.factor.message}</div>} {/* Changed to div and mat-error */}
                </div>
                </>)}
                {/* ===== Cycle Time ===== */}
                <div className={`form_field ${errors.cycle_time ? "error-outline" : ""}`}>
                  <DemoItem className="white-label">
                    <TimePicker
                      {...register("cycle_time", {
                        required: "Cycle Time is required",
                        validate: (value) => {
                          if (!value) return "Cycle Time is required";
                          if (!dayjs(value).isValid()) return "Invalid time format";
                          return true;
                        },
                      })}
                      onBlur={() => trigger("cycle_time")}
                      value={shiftForm.cycle_time}
                      onChange={(value) => handleTimeChange("cycle_time", value)}
                      views={["hours", "minutes", "seconds"]}
                      format="HH:mm:ss"
                      ampm={false}
                      label="Cycle Time *"
                      error={!!errors.cycle_time}
                      slotProps={{
                        textField: {
                          helperText: errors.cycle_time?.message,
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: errors.cycle_time ? "red" : "black",
                          },
                          "&:hover fieldset": { borderColor: "black" },
                          "&.Mui-focused fieldset": {
                            borderColor: errors.cycle_time ? "red" : "orange",
                          },
                          "& .MuiOutlinedInput-input": { color: "black" },
                          "&.Mui-focused .MuiOutlinedInput-input": { caretColor: "orange" },
                        },
                      }}
                    />
                  </DemoItem>
                </div>

                {/* ===== Handling Time ===== */}
                <div className={`form_field ${errors.handling_time ? "error-outline" : ""}`}>
                  <DemoItem>
                    <TimePicker
                      {...register("handling_time", {
                        required: "Handling Time is required",
                        validate: (value) => {
                          if (!value) return "Handling Time is required";
                          if (!dayjs(value).isValid()) return "Invalid time format";
                          return true;
                        },
                      })}
                      onBlur={() => trigger("handling_time")}
                      value={shiftForm.handling_time}
                      onChange={(value) => handleTimeChange("handling_time", value)}
                      views={["hours", "minutes", "seconds"]}
                      format="HH:mm:ss"
                      ampm={false}
                      label="Handling Time *"
                      error={!!errors.handling_time}
                      slotProps={{
                        textField: {
                          helperText: errors.handling_time?.message,
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: errors.handling_time ? "red" : "black",
                          },
                          "&:hover fieldset": { borderColor: "black" },
                          "&.Mui-focused fieldset": {
                            borderColor: errors.handling_time ? "red" : "orange",
                          },
                          "& .MuiOutlinedInput-input": { color: "black" },
                          "&.Mui-focused .MuiOutlinedInput-input": { caretColor: "orange" },
                        },
                      }}
                    />
                  </DemoItem>
                </div>

                {/* ===== Setup Time ===== */}
                <div className={`form_field ${errors.setupTime ? "error-outline" : ""}`}>
                  <DemoItem>
                    <TimePicker
                      {...register("setupTime", {
                        required: "Setup Time is required",
                        validate: (value) => {
                          if (!value) return "Setup Time is required";
                          if (!dayjs(value).isValid()) return "Invalid time format";
                          return true;
                        },
                      })}
                      onBlur={() => trigger("setupTime")}
                      value={shiftForm.setupTime}
                      onChange={(value) => handleTimeChange("setupTime", value)}
                      views={["hours", "minutes", "seconds"]}
                      format="HH:mm:ss"
                      ampm={false}
                      label="Setup Time *"
                      error={!!errors.setupTime}
                      slotProps={{
                        textField: {
                          helperText: errors.setupTime?.message,
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: errors.setupTime ? "red" : "black",
                          },
                          "&:hover fieldset": { borderColor: "black" },
                          "&.Mui-focused fieldset": {
                            borderColor: errors.setupTime ? "red" : "orange",
                          },
                          "& .MuiOutlinedInput-input": { color: "black" },
                          "&.Mui-focused .MuiOutlinedInput-input": { caretColor: "orange" },
                        },
                      }}
                    />
                  </DemoItem>
                </div>


                {/* Operation Number Field 
                <div className={`form_field ${errors.jobcard ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("jobcard", { required: "Job Card is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      validate: value =>
                        /^[a-zA-Z0-9\s]*$/.test(value) || "Special characters are not allowed" })}
                    label="Raw Material"
                    type="text"
                    name="jobcard"
                    value={shiftForm.jobcard}
                    onChange={handleFormChange}
                    error={!!errors.jobcard}
                    inputProps={{ maxLength: 100}}
                    InputLabelProps={{
                      required: true, 
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.jobcard && <div className="mat-error">{errors.jobcard.message}</div>}
                </div>
                <div className={`form_field ${errors.drawingcode ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("drawingcode", { required: "Drawingcode is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      validate: value => {
                        if (isNaN(value)) return "Drawing code  must be a number";
                        if (Number(value) <= 0) return "Drawing code must be greater than 0";
                        return true;
                      }
                     })}
                    label="Raw Material Code"
                    type="number"
                    name="drawingcode"
                    value={shiftForm.drawingcode}
                    onChange={handleFormChange}
                    error={!!errors.drawingcode}
                    inputProps={{ maxLength: 100, min: 1 }}
                    InputLabelProps={{
                      required: true, 
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.drawingcode && <div className="mat-error">{errors.drawingcode.message}</div>}
                </div>  */}

              </div>
            </LocalizationProvider>
            <div className="form-button text-right" align="end" style={{ marginRight: '10px' }}>
              <Button type="submit" variant="contained" className="filter_btn btn_orange" color="warning" >
                Save
              </Button>
            </div>
            <br></br>
          </form>
        </div>
      </div>
    </Dialog>
  );
}